import { createSelector } from 'reselect';

import { ClarisApiError } from '../../domain/error';
import { LoadingState } from '../../domain/schemas';
import { SettingsOptionsPlanProgressTransition } from '../../domain/settingsOptionsPlanProgressTransition';

import { RootState } from '../../store';
import {
  filterAiPredictionFields,
  hasAIFlagHall,
} from '../../utils/hallAiFlag';
import { settingsOptionsPlanProgressTransitionDefaultValue } from '../../utils/settingsOptionsPlanProgressTransitionDefaultValue';
import {
  dataPlanProgressTransitionSettingAreaSelector,
  dataPlanProgressTransitionSettingSelector,
} from './dataPlanProgressTransition';

/* ---------------------------------------------------------------
 * Action Types
 */

const INIT_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION =
  'INIT_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION' as const;
const FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION =
  'FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION' as const;
const FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_REQUEST =
  'FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_REQUEST' as const;
const FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_SUCCESS =
  'FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_SUCCESS' as const;
const RENEW_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION =
  'RENEW_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION' as const;

export const SettingsOptionsPlanProgressTransitionActionTypes = {
  INIT_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION,
  FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION,
  FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_REQUEST,
  FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_SUCCESS,
  RENEW_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION,
};

/* ---------------------------------------------------------------
 * Action Creators
 */

function initSettingsOptionsPlanProgressTransitionAction() {
  return {
    type: INIT_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION,
  };
}

function fetchSettingsOptionsPlanProgressTransitionAction() {
  return {
    type: FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION,
  };
}

function fetchSettingsOptionsPlanProgressTransitionRequestAction() {
  return {
    type: FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_REQUEST,
  };
}

function fetchSettingsOptionsPlanProgressTransitionSuccessAction(
  settingsOptionsPlanProgressTransition: SettingsOptionsPlanProgressTransition
) {
  return {
    type: FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_SUCCESS,
    payload: { settingsOptionsPlanProgressTransition },
  };
}

function renewSettingsOptionsPlanProgressTransitionAction() {
  return {
    type: RENEW_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION,
  };
}

export const SettingsOptionsPlanProgressTransitionActionCreators = {
  initSettingsOptionsPlanProgressTransitionAction,
  fetchSettingsOptionsPlanProgressTransitionAction,
  fetchSettingsOptionsPlanProgressTransitionRequestAction,
  fetchSettingsOptionsPlanProgressTransitionSuccessAction,
  renewSettingsOptionsPlanProgressTransitionAction,
};

/* ---------------------------------------------------------------
 * Actions
 */

type SettingsOptionsPlanProgressTransitionAction =
  | ReturnType<typeof initSettingsOptionsPlanProgressTransitionAction>
  | ReturnType<typeof fetchSettingsOptionsPlanProgressTransitionAction>
  | ReturnType<typeof fetchSettingsOptionsPlanProgressTransitionRequestAction>
  | ReturnType<typeof fetchSettingsOptionsPlanProgressTransitionSuccessAction>
  | ReturnType<typeof renewSettingsOptionsPlanProgressTransitionAction>;

/* ---------------------------------------------------------------
 * State
 */

type SettingsOptionsPlanProgressTransitionState = {
  loadingState: LoadingState;
  settingsOptionsPlanProgressTransition?: SettingsOptionsPlanProgressTransition;
  error?: ClarisApiError;
};

const initialState: SettingsOptionsPlanProgressTransitionState = {
  loadingState: 'prepare',
  settingsOptionsPlanProgressTransition: undefined,
  error: undefined,
};

/* ---------------------------------------------------------------
 * Selector
 */

// settingsOptionsを全て取得する
export const settingsOptionsPlanProgressTransitionsettingsOptionsSelector = (
  state: RootState
) => {
  const settings =
    state.settingsOptionsPlanProgressTransition
      .settingsOptionsPlanProgressTransition;
  // MEMO: コンポーネント側がundefined許容しないため、空の場合空データを返す
  return settings
    ? settings
    : settingsOptionsPlanProgressTransitionDefaultValue;
};

// 店舗一覧を取得する
export const settingsOptionsPlanProgressTransitionSelector = (
  state: RootState
) => {
  const settingsOptionsPlanProgressTransition =
    state.settingsOptionsPlanProgressTransition
      .settingsOptionsPlanProgressTransition;

  return settingsOptionsPlanProgressTransition
    ? settingsOptionsPlanProgressTransition.searchCondition
    : settingsOptionsPlanProgressTransitionDefaultValue.searchCondition;
};

// APIで取得した検索条件を取得する
export const settingsOptionsPlanProgressTransitionSearchConditionSelector = (
  state: RootState
) => {
  const settingsOptionsPlanProgressTransition =
    state.settingsOptionsPlanProgressTransition
      .settingsOptionsPlanProgressTransition;

  return settingsOptionsPlanProgressTransition
    ? settingsOptionsPlanProgressTransition.searchCondition
    : settingsOptionsPlanProgressTransitionDefaultValue.searchCondition;
};

export const settingsOptionsPlanProgressTransitionLoadingStateSelector = (
  state: RootState
) => {
  return state.settingsOptionsPlanProgressTransition.loadingState;
};

/**
 * 検索されたエリアのホール一覧を取得する
 */
export const settingsOptionsPlanProgressTransitionSearchConditionHallsBySearchedAreaSelector =
  createSelector(
    [
      settingsOptionsPlanProgressTransitionSearchConditionSelector,
      dataPlanProgressTransitionSettingAreaSelector,
    ],
    ({ halls, areas }, selectedAreaIds) => {
      if (!selectedAreaIds) {
        return halls ?? [];
      }
      const hallCodesInAreas = areas
        .filter((x) => selectedAreaIds.includes(x.id))
        .flatMap((x) => x.halls);

      return halls.filter((x) => hallCodesInAreas.includes(x.code));
    }
  );

/**
 * デフォルトの表示項目のリストを取得する
 */
export const settingsOptionsPlanProgressTransitionDefaultFieldsSelector =
  createSelector(
    settingsOptionsPlanProgressTransitionsettingsOptionsSelector,
    // MEMO: デフォルトの表示項目は、fieldsのisDefaultがtrueのもの
    (settings) => settings.fields.transition.filter((x) => x.isDefault)
  );

/**
 * サブ表示項目のリストを取得する
 */
export const settingsOptionsPlanProgressTransitionFieldsSelector =
  createSelector(
    settingsOptionsPlanProgressTransitionsettingsOptionsSelector,
    (settings) => settings.fields.transition
  );

/**
 * AI予測項目の列codeのSetを取得する（テーブルヘッダー・セルの強調表示に使用）
 * setting/optionsのfields.transitionのうちisAiPredictionがtrueの項目のcodeを返す
 */
export const settingsOptionsPlanProgressTransitionAiPredictionCodesSelector =
  createSelector(
    settingsOptionsPlanProgressTransitionsettingsOptionsSelector,
    (settings) =>
      new Set(
        settings.fields.transition
          .filter(
            (field): field is typeof field & { code: string } =>
              !!(field.isAiPrediction && field.code)
          )
          .map((field) => field.code)
      )
  );

/**
 * メイン表示項目のリストを取得する
 */
export const settingsOptionsPlanProgressTransitionColumnGroupsSelector =
  createSelector(
    settingsOptionsPlanProgressTransitionsettingsOptionsSelector,
    (settings) => settings.columnGroups
  );

/**
 * 選択されているホールのAIFlagフラグをチェックする
 * dataPlanProgressTransitionのsetting.halls（選択されているホールコードの配列）と、
 * settingsOptionsPlanProgressTransitionのsearchCondition.halls（HallWithFlag[]）を照合し、
 * 選択されているホールコードに対応するhallsのisFTがtrueのものが1つでもあるかを判定
 *
 * 全店表示（halls が空配列）のときは false。
 * （AI予測データは店舗単位のため、ツールバーのAI予測日など「店舗指定時のみ」のUIに使用する）
 */
export const settingsOptionsPlanProgressTransitionHasAIFlagHallSelector =
  createSelector(
    [
      dataPlanProgressTransitionSettingSelector,
      settingsOptionsPlanProgressTransitionSearchConditionSelector,
    ],
    (setting, searchCondition) => {
      if (!setting?.halls || setting.halls.length === 0) {
        return false;
      }
      const selectedHallCodes = setting.halls;
      const halls = searchCondition.halls ?? [];
      return hasAIFlagHall(selectedHallCodes, halls);
    }
  );

/**
 * AI予測関連UIを表示可能かどうかを判定する。
 * 表示項目パネルの AI 予測列の出し分け、テーブル上の AI 着地見込ブロックなどに利用する。
 *
 * ホール選択時は、選択中ホールのうち isFT が true の店舗が1件でもあれば true。
 * 全店表示（setting.halls が空）のときは、検索対象エリア内の店舗に isFT が1件でもあれば true。
 * （全店でも AI 系 UI を出し、店舗指定時との切り替えを可能にする。HasAIFlagHall とは別判定）
 */
export const settingsOptionsPlanProgressTransitionShowAiPredictionUiSelector =
  createSelector(
    [
      dataPlanProgressTransitionSettingSelector,
      settingsOptionsPlanProgressTransitionSearchConditionSelector,
      settingsOptionsPlanProgressTransitionSearchConditionHallsBySearchedAreaSelector,
    ],
    (setting, searchCondition, areaHalls) => {
      const halls = searchCondition.halls ?? [];
      const selectedHallCodes = setting?.halls;
      if (selectedHallCodes && selectedHallCodes.length > 0) {
        return hasAIFlagHall(selectedHallCodes, halls);
      }
      const areaHallCodes = areaHalls.map((h) => h.code);
      return hasAIFlagHall(areaHallCodes, halls);
    }
  );

/**
 * メイン表示項目とサブ表示項目を結合したリストを取得する
 * settingsOptionsPlanProgressTransitionFieldsSelectorとsettingsOptionsPlanProgressTransitionColumnGroupsSelectorを結合する
 * 結合の条件としては、columnGroupsのcodeとtransition[?].columnGroupが一致するものを結合する
 * showAiPredictionUi が false の場合、isAiPrediction が true の項目をフィルタリングする
 */
export const settingsOptionsPlanProgressTransitionFieldsColumnGroupSelector =
  createSelector(
    [
      settingsOptionsPlanProgressTransitionFieldsSelector,
      settingsOptionsPlanProgressTransitionColumnGroupsSelector,
      settingsOptionsPlanProgressTransitionShowAiPredictionUiSelector,
    ],
    (subFields, mainFields, showAiPredictionUi) => {
      const filteredSubFields = filterAiPredictionFields(
        subFields,
        showAiPredictionUi
      );

      //mainFieldsのcodeとsubFieldsのcolumnGroupが一致した場合、mainFieldsを親としてsubFieldsを子とした配列を作成
      const mainFieldsWithSubFields = mainFields.map((mainField) => {
        const subFieldsArray = filteredSubFields.filter((subField) =>
          subField.columnGroup?.includes(mainField.code)
        );
        return { ...mainField, subFields: subFieldsArray };
      });
      return mainFieldsWithSubFields;
    }
  );

/* ---------------------------------------------------------------
 * Reducer
 */

export function settingsOptionsPlanProgressTransitionReducer(
  state = initialState,
  action: SettingsOptionsPlanProgressTransitionAction
): SettingsOptionsPlanProgressTransitionState {
  switch (action.type) {
    case FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
        settingsOptionsPlanProgressTransition:
          action.payload.settingsOptionsPlanProgressTransition,
      };
    case RENEW_SETTINGS_OPTIONS_PLAN_PROGRESS_TRANSITION:
      return initialState;
    default:
      return state;
  }
}
